import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Location} from '@angular/common';
import {TerceroUpPasarelaTarifaService} from '../../../_services/tercero-up-pasarela-tarifa.service';
import {TerceroUPPasarelaTarifa} from '../../../_models/tercero-up-pasarela-tarifa';
import {NavService} from '../../../_services/_nav.service';
import {AppSettingService} from '../../../_services/app-setting.service';
import {
  ADMSB_CONCEPTOS_TARIFA,
  AdmsbCanalTarifa,
  AdmsbConceptoTarifa,
  AdmsbConceptoTarifaDef,
  AdmsbMedioTarifa,
} from '../../../_models/admsb-tarifa';

@Component({
  selector: 'app-admsb-tarifas',
  templateUrl: './admsb-tarifas.component.html',
  styleUrls: ['./admsb-tarifas.component.scss'],
})
export class AdmsbTarifasComponent implements OnInit {

  idTitular: string;
  idTerceroUPPasarelaProducto: string;

  canales: AdmsbCanalTarifa[] = [];
  cargando = true;

  private snapshotsPorMedio = new Map<string, AdmsbConceptoTarifa[]>();

  /** Ids de los medios cuyo guardado está en curso; evita doble envío */
  private guardandoMedios = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private terceroUpPasarelaTarifaServicio: TerceroUpPasarelaTarifaService,
    private navService: NavService,
    private appSettingService: AppSettingService,
  ) {
  }

  ngOnInit(): void {
    this.idTitular = this.route.snapshot.paramMap.get('id');
    this.idTerceroUPPasarelaProducto = this.route.snapshot.paramMap.get('idTerceroUPPasarelaProducto');

    this.terceroUpPasarelaTarifaServicio.listMetodosPasarelaTarifas(this.idTerceroUPPasarelaProducto).subscribe({
      next: filas => {
        this.canales = this.agruparPorCanalYMedio(filas);
        this.cargando = false;
      },
      error: () => this.cargando = false,
    });
  }

  regresar(): void {
    this.location.back();
  }

  /**
   * Agrupa la lista plana del servicio en canales -> medios
   */
  private agruparPorCanalYMedio(filas: TerceroUPPasarelaTarifa[]): AdmsbCanalTarifa[] {
    const canales: AdmsbCanalTarifa[] = [];

    filas.forEach(fila => {
      let canal = canales.find(c => c.id === fila.idCanal);
      if (!canal) {
        canal = {id: fila.idCanal, nombre: fila.nombreCanal, medios: []};
        canales.push(canal);
      }

      const medio: AdmsbMedioTarifa = {
        id: fila.idProductoPasarelaTarifa,
        nombre: fila.nombreMedioPago,
        raw: fila,
        aplicaTarifaEspecial: !!fila.indicadorAplicaTarifaEspecial,
        conceptos: ADMSB_CONCEPTOS_TARIFA.map(def => ({
          campo: def.campo,
          tarifaBase: fila[def.campoBase] as number,
          tarifaPreferencial: (fila[def.campo] as number) ?? 0,
        })),
      };
      canal.medios.push(medio);
      this.guardarSnapshot(medio);
    });

    return canales;
  }

  private guardarSnapshot(medio: AdmsbMedioTarifa): void {
    this.snapshotsPorMedio.set(medio.id, medio.conceptos.map(c => ({...c})));
  }

  cancelarMedio(medio: AdmsbMedioTarifa): void {
    const snapshot = this.snapshotsPorMedio.get(medio.id);
    if (snapshot) {
      medio.conceptos = snapshot.map(c => ({...c}));
    }
    this.navService.setMesage(0, {
      severity: 'info',
      summary: this.navService.buscarMensaje('LBL_INFORMACION'),
      detail: this.navService.buscarMensaje('ADMINISTRADORSERVICIOSBANCARIOS.MSG_CAMBIOSCANCELADOS'),
    });
  }

  /** Indica si el medio tiene un guardado en curso */
  estaGuardando(medio: AdmsbMedioTarifa): boolean {
    return this.guardandoMedios.has(medio.id);
  }

  guardarMedio(medio: AdmsbMedioTarifa): void {
    if (!medio.raw || this.estaGuardando(medio)) {
      return;
    }

    const registro: TerceroUPPasarelaTarifa = {...medio.raw, indicadorHabilitado: true};
    medio.conceptos.forEach(c => registro[c.campo] = c.tarifaPreferencial);

    this.guardandoMedios.add(medio.id);
    this.terceroUpPasarelaTarifaServicio.guardarPasarelaVersion(registro).subscribe({
      next: (respuesta) => {
        medio.raw = {
          ...medio.raw,
          id: respuesta.id,
          idTerceroUPPasarelaTarifa: respuesta.idTerceroUPPasarelaTarifa,
          idTercerosUPPasarelasTarifasVersion: respuesta.idTercerosUPPasarelasTarifasVersion,
          indicadorHabilitado: respuesta.indicadorHabilitado,
          auditoriaFecha: respuesta.auditoriaFecha,
          auditoriaUsuario: respuesta.auditoriaUsuario,
          ip: respuesta.ip,
          tarifaTrxVentaPorcentaje: respuesta.tarifaTrxVentaPorcentaje,
          tarifaTrxIndividual: respuesta.tarifaTrxIndividual,
          montoMinimoTrx: respuesta.montoMinimoTrx,
          montoMaximoTrx: respuesta.montoMaximoTrx,
          montoMaximoMes: respuesta.montoMaximoMes,
          limiteTrxDiario: respuesta.limiteTrxDiario,
          limiteTrxMes: respuesta.limiteTrxMes,
        };
        this.guardarSnapshot(medio);
        this.guardandoMedios.delete(medio.id);
        this.navService.setMesage(2);
      },
      error: () => {
        this.guardandoMedios.delete(medio.id);
        this.navService.setMesage(3);
      },
    });
  }

  definicion(campo: string): AdmsbConceptoTarifaDef {
    return ADMSB_CONCEPTOS_TARIFA.find(def => def.campo === campo);
  }

  prefijo(campo: string): string {
    return this.definicion(campo)?.tipo === 'moneda' ? `${this.appSettingService.simboloDinero} ` : '';
  }

  sufijo(campo: string): string {
    return this.definicion(campo)?.tipo === 'porcentaje' ? ` ${this.appSettingService.percentageSymbol}` : '';
  }

  maxEnteros(campo: string): number {
    return this.definicion(campo)?.tipo === 'porcentaje' ? 3 : 12;
  }

  maxDecimales(campo: string): number {
    return 3;
  }

}
